import { useEffect, useState } from 'react';
import { api } from '../api.js';

interface Row { id: number; at: string; actorKind: string; actorName: string; action: string; summary: string }
interface Snap { id: string; at: string; reason: string }
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const PAGE_SIZE = 50; // matches the server's default page size for GET .../activity

export function HistoryPanel({ projectId, role }: { projectId: string; role: 'owner' | 'member' }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [snaps, setSnaps] = useState<Snap[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const loadActivity = (before?: number) =>
    api<{ activity: Row[] }>('GET', `/api/projects/${projectId}/activity${before === undefined ? '' : `?before=${before}`}`)
      .then((r) => {
        setRows((prev) => (before === undefined ? r.activity : [...prev, ...r.activity]));
        setHasMore(r.activity.length >= PAGE_SIZE);
      })
      .catch((e) => setError((e as Error).message));

  const loadSnapshots = () =>
    api<{ snapshots: Snap[] }>('GET', `/api/projects/${projectId}/snapshots`)
      .then((r) => setSnaps(r.snapshots))
      .catch((e) => setError((e as Error).message));

  const load = () => {
    void loadActivity();
    void loadSnapshots();
  };
  useEffect(load, [projectId]);

  const showOlder = () => {
    const oldest = rows[rows.length - 1];
    if (oldest) void loadActivity(oldest.id);
  };

  const takeSnapshot = async () => {
    setError('');
    setTakingSnapshot(true);
    try {
      await api('POST', `/api/projects/${projectId}/snapshots`);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setTakingSnapshot(false);
    }
  };

  const restore = async (id: string) => {
    setError('');
    setRestoring(true);
    try {
      const r = await api<{ restoredAt: string }>('POST', `/api/projects/${projectId}/snapshots/${id}/restore`);
      setMessage(`Restored to ${when(r.restoredAt)}. The state before restoring was saved as a snapshot.`);
      setConfirm(null);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <section className="history">
      {error && <p className="error" role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      <div>
        <h2>Activity</h2>
        <ol className="activity">
          {rows.map((r) => (
            <li key={r.id}>
              <strong>{r.actorKind === 'agent' ? '🤖 ' : ''}{r.actorName}</strong> {r.summary} <small>{when(r.at)}</small>
            </li>
          ))}
          {rows.length === 0 && <li className="muted">Nothing yet.</li>}
        </ol>
        {hasMore && <button className="link" onClick={showOlder}>Show older</button>}
      </div>
      <div>
        <h2>Snapshots</h2>
        <button disabled={takingSnapshot} onClick={() => void takeSnapshot()}>Take a snapshot now</button>
        <ol className="snapshots">
          {snaps.map((s) => (
            <li key={s.id}>
              {when(s.at)} <small>{s.reason === 'auto' ? 'automatic' : s.reason === 'pre-restore' ? 'before a restore' : 'manual'}</small>
              {role === 'owner' &&
                (confirm === s.id ? (
                  <>
                    <button className="danger" disabled={restoring} onClick={() => void restore(s.id)}>Restore for everyone</button>
                    <button className="link" disabled={restoring} onClick={() => setConfirm(null)}>Cancel</button>
                  </>
                ) : (
                  <button className="link" onClick={() => setConfirm(s.id)}>Restore…</button>
                ))}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
